/**
 * Sale Announcement Bar — Sticky Positioning
 *
 * Dynamically sets the CSS variable --sale-bar-top on the sale announcement bar
 * so it sticks directly below the visible header with absolute precision.
 */
(function () {
  'use strict';

  const TRANSITION_SPEED = 300; // matches --header-transition-speed in header

  /** Updates --sale-bar-top on the sale bar by measuring the physical header height. */
  function updateSaleBarTop(saleBar, storeHeader) {
    if (!saleBar || !storeHeader) return;

    // Check if sticky header is enabled
    const isHeaderSticky = storeHeader.dataset.isSticky === 'true' || storeHeader.closest('.cc-header--sticky') !== null;

    if (isHeaderSticky) {
      // The header sticks at top: -1px, so its bottom edge is at visibleHeight - 1px in the viewport.
      const visibleHeaderHeight = storeHeader.getBoundingClientRect().height;
      saleBar.style.setProperty('--sale-bar-top', `${visibleHeaderHeight - 1}px`);
    } else {
      // If the header is not sticky, the sale bar sticks at the top of the viewport.
      saleBar.style.setProperty('--sale-bar-top', '0px');
    }
  }

  function init() {
    const saleBar = document.querySelector('.cc-sale-announcement-bar');
    const storeHeader = document.querySelector('store-header');
    if (!saleBar || !storeHeader) return;

    // Initial position on load
    updateSaleBarTop(saleBar, storeHeader);

    // --- Frame-by-frame animation tracking during transitions ---
    let animationFrameId = null;
    let transitionStartTime = null;

    function animatePosition() {
      updateSaleBarTop(saleBar, storeHeader);
      const elapsed = Date.now() - transitionStartTime;
      if (elapsed < TRANSITION_SPEED + 100) {
        animationFrameId = requestAnimationFrame(animatePosition);
      } else {
        animationFrameId = null;
      }
    }

    function startTransitionAnimation() {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      transitionStartTime = Date.now();
      animatePosition();
    }

    // --- React to class changes on the header (like is-out state change) ---
    const observer = new MutationObserver(startTransitionAnimation);
    observer.observe(storeHeader, {
      attributes: true,
      attributeFilter: ['class']
    });

    // --- React to layout/style/mutation changes inside the header ---
    const headerHeightObserver = new MutationObserver(function () {
      updateSaleBarTop(saleBar, storeHeader);
    });
    headerHeightObserver.observe(storeHeader, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'hidden']
    });

    // --- React to window scroll ---
    window.addEventListener('scroll', function () {
      updateSaleBarTop(saleBar, storeHeader);
    }, { passive: true });

    // --- React to window resize ---
    let resizeTimer;
    window.addEventListener('resize', function () {
      updateSaleBarTop(saleBar, storeHeader);
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        updateSaleBarTop(saleBar, storeHeader);
      }, 150);
    });

    // --- React to breakpoint changes (theme custom event) ---
    window.addEventListener('on:breakpoint-change', startTransitionAnimation);

    // --- React to mobile menu toggle ---
    const menuToggle = document.querySelector('.main-menu__toggle');
    if (menuToggle) {
      menuToggle.addEventListener('click', startTransitionAnimation);
    }

    // --- React to search bar toggle ---
    const searchToggle = document.querySelector('.js-show-search');
    const searchToggleLeft = document.querySelector('.js-show-search-left');
    if (searchToggle) searchToggle.addEventListener('click', startTransitionAnimation);
    if (searchToggleLeft) searchToggleLeft.addEventListener('click', startTransitionAnimation);

    // --- React to complete page load (fonts/images) ---
    window.addEventListener('load', function () {
      updateSaleBarTop(saleBar, storeHeader);
    });
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
